import { mapState } from 'vuex';
import Sortable from 'sortablejs';
import ApiService from '@/services/apiService';
import { get, isObject, cloneDeep } from 'lodash';
import { getFileURL } from '@/utils/file-uploads';
import dataSourcesListMixin from '@/components/core/dtwin/state/dataSourcesListMixin';
import iconsMixin from '@/components/core/dtwin/state/bridge/iconsMixin';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['id'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
const excludedKeys = ['_raw', '_parsed', '_health'];
export default {
  name: 'DeviceTemplatesDataStates',
  mixins: [dataSourcesListMixin, iconsMixin],
  props: {
    template: {
      type: Object,
      required: true
    },
    isProductiveVersion: {
      type: Boolean,
      optional: true,
      default: false
    },
    templateInformation: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      optional: true,
      default: false
    }
  },
  data() {
    return {
      isDataStatesFormValid: true,
      showDeleteDialog: false,
      confirmDeleteKey: null,
      deviceDriver: null,
      tableOptions: {
        ...defaultTableOptions
      },
      targetVariables: [],
      bridgeConfigs: [],
      stateEditItem: null,
      stateEditDialog: false,
      linkedStateAggregations: []
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    themeMode() {
      return Boolean(this.$store.state.app.UiTheme) ? 'dark' : 'light';
    },
    tableLocalizations() {
      return {
        headers: [{
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.targetVariable')
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.type')
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.unit')
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.addState'),
          align: 'center'
        }],
        dataStatesHeaders: [{
          text: ''
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.icon')
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.title')
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.type')
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.unit')
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.source'),
          align: 'center'
        }, {
          text: ''
        }]
      };
    },
    tableItems() {
      return Object.keys(this.targetVariables).map(key => ({
        properties: {
          key: this.targetVariables[key].name,
          title: this.targetVariables[key].name,
          valueType: this.targetVariables[key].type,
          unit: this.targetVariables[key].unit
        }
      }));
    },
    addedStatesItems() {
      return this.template.stateDefinition || [];
    },
    actionButtons() {
      return [{
        disabled: this.isProductiveVersion || this.readonly,
        icon: 'fal fa-plus',
        action: 'addItem',
        dataCy: 'add_state',
        title: this.$i18n_t('Common.add'),
        tooltip: this.$i18n_t('Common.addNew')
      }];
    },
    dataStatesHeaders() {
      return this.isProductiveVersion || this.readonly ? this.tableLocalizations.dataStatesHeaders.slice(1) : this.tableLocalizations.dataStatesHeaders;
    }
  },
  watch: {
    template: {
      handler() {
        if (!this.template || !this.template.deviceDriverId) {
          return;
        }
        this.loadDeviceDriverDetails();
        this.loadBridgeConfigs();
      },
      immediate: true
    }
  },
  async mounted() {
    this.initSortableTable();
  },
  methods: {
    onStateEditUpdate(newState) {
      this.stateEditItem = newState;
    },
    async loadDeviceDriverDetails() {
      const deviceDriverId = this.template.deviceDriverId;
      try {
        this.deviceDriver = await ApiService.virtualDeviceDrivers.fetchOneById(deviceDriverId);
        this.targetVariables = this.getTargetVariables(this.deviceDriver);
      } catch (error) {
        this.$snack(get(error, 'response.data.err') || this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.fetchDeviceDriverFailed'), 'error');
      }
    },
    getTargetVariables(deviceDriver) {
      const driverVersion = deviceDriver.versions[0];
      let parserVariables = get(driverVersion, 'parser.config.variables', {});
      const isMultiParser = get(driverVersion, 'parser.type') === 'multi';
      if (isMultiParser) {
        parserVariables = {};
        const parserKeys = Object.keys(get(driverVersion, 'parser.config') || {}).filter(k => k !== '__switch');
        parserKeys.forEach(pKey => {
          const parserVariablesOfKey = get(driverVersion, `parser.config.${pKey}.variables`);
          if (isObject(parserVariablesOfKey)) {
            Object.assign(parserVariables, parserVariablesOfKey);
          }
        });
      }
      const mappedParserVariables = Object.keys(parserVariables).filter(key => !excludedKeys.includes(key)).map(key => {
        return {
          name: parserVariables[key].name,
          type: parserVariables[key].type,
          unit: get(parserVariables[key], 'value_type.name')
        };
      });
      return mappedParserVariables;
    },
    getImageUrl(fileId) {
      return getFileURL(fileId, 2);
    },
    addItem() {
      this.openStateEditForm();
    },
    openStateEditForm() {
      this.stateEditItem = null;
      this.stateEditDialog = true;
    },
    isStateDeletable(state) {
      return state.key !== '_raw' && state.key !== '_health' && state.key !== '_parsed' && !this.readonly;
    },
    partOfDataStates(item) {
      return this.template.stateDefinition.find(i => i.properties.bindingVariable === item.properties.title);
    },
    getValueType(unit) {
      if (unit === 'Number') {
        return 'number';
      } else if (unit === 'String') {
        return 'string';
      } else if (unit === 'Boolean') {
        return 'boolean';
      } else if (unit === 'Unset') {
        return 'number';
      } else {
        return 'number';
      }
    },
    async addState(item) {
      const itemToAdd = cloneDeep(item);
      itemToAdd.key = item.properties.key;
      itemToAdd.properties = {
        unit: item.properties.unit,
        title: item.properties.title,
        visual: {},
        binding: {
          type: 'bridge',
          options: {
            variable: item.properties.key,
            configId: this.template.deviceMeta.connectorId
          },
          transformer: {
            script: 'module.exports = (data) => {\n  return data;\n}'
          }
        },
        valueType: this.getValueType(item.properties.unit),
        bindingVariable: item.properties.key
      };
      try {
        await ApiService.deviceTemplateUpdate(this.template);
        this.template.stateDefinition.push(itemToAdd);
        this.$emit('update-unsaved-changes', this.template);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.stateAdded', {
          key: itemToAdd.key
        }), 'success');
      } catch (e) {
        if (e.message === 'HEALTH_OPEARTOR_NOT_ALLOWED') {
          this.$snack(this.$i18n_t('Error.HEALTH_OPEARTOR_NOT_ALLOWED', {
            operator: get(e, 'response.data.data.operator'),
            valueType: get(e, 'response.data.data.valueType')
          }), 'error');
        } else {
          this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.stateAddingFailed', {
            key: itemToAdd.key
          }), 'error');
        }
      }
    },
    async handleConfirmDelete() {
      this.template.stateDefinition = this.template.stateDefinition.filter(state => state.key !== this.confirmDeleteKey);
      try {
        await ApiService.deviceTemplateUpdate(this.template);
        this.$emit('update-unsaved-changes', this.template);
        this.showDeleteDialog = false;
        this.linkedStateAggregations = [];
        this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.states.deleted') + this.confirmDeleteKey, 'success');
      } catch (e) {
        if (e.message === 'HEALTH_OPEARTOR_NOT_ALLOWED') {
          this.$snack(this.$i18n_t('Error.HEALTH_OPEARTOR_NOT_ALLOWED', {
            operator: get(e, 'response.data.data.operator'),
            valueType: get(e, 'response.data.data.valueType')
          }), 'error');
        } else if (e.response && this.$i18n_t(`Error.${e.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${e.response.data.message}`), 'error');
        } else {
          this.$snack('Could not delete state', 'error');
        }
      } finally {
        this.confirmDeleteKey = null;
      }
    },
    async loadBridgeConfigs() {
      ApiService.getBridgesForTenant(this.template.tenantId).then(configs => {
        this.bridgeConfigs = configs.filter(config => config.id === get(this.template, 'deviceMeta.connectorId')).map(config => {
          return {
            type: config.type,
            image: this.bridgeTypesIcons[config.type],
            title: config.name,
            configId: config.id,
            tenantId: config.tenantId
          };
        });
      }).catch(error => {
        console.error('Error', error);
      });
    },
    bridgeConfig(bridge, types) {
      const foundMatch = types.find(config => {
        return config.type === bridge.type;
      });
      return foundMatch;
    },
    editState(item) {
      if (item.key === '_health') {
        let baseUrl = window.location.href.split('?')[0];
        let queryParams = new URLSearchParams(window.location.search);
        queryParams.set('tab', 'tab-health-configuration');
        window.location.href = `${baseUrl}?${queryParams.toString()}`;
        return;
      }
      const indexOfEditedElement = this.template.stateDefinition.findIndex(el => el.key === item.key);
      this.stateEditItem = item;
      this.stateEditItem.indexOfEditedElement = indexOfEditedElement;
      this.stateEditDialog = true;
    },
    checkForDependencies(key) {
      this.template.stateDefinition.forEach(stateDef => {
        const bindingType = get(stateDef, 'properties.binding.type', '');
        let isDependency = false;
        if (bindingType === 'virtual-device-aggregate') {
          isDependency = get(stateDef, 'properties.binding.options.virtualDevice.stateIds', []).includes(key);
        } else if (bindingType === 'timeseries-aggregate') {
          isDependency = get(stateDef, 'properties.binding.options.timeAggregation.bindedKey', '') === key;
        }
        if (isDependency) {
          this.linkedStateAggregations.push({
            title: stateDef.key,
            icon: get(stateDef, 'properties.faIcon', '') || 'layer-group'
          });
        }
      });
    },
    deleteState(key) {
      this.checkForDependencies(key);
      this.confirmDeleteKey = key;
      this.showDeleteDialog = true;
    },
    closeEditWithError(errorMessage) {
      if (errorMessage) {
        this.$snack(errorMessage, 'error');
      }
      this.stateEditDialog = false;
      this.stateEditItem = null;
    },
    async saveState(stateDetails) {
      if (stateDetails.indexOfEditedElement || stateDetails.indexOfEditedElement === 0) {
        this.template.stateDefinition.splice(stateDetails.indexOfEditedElement, 1, stateDetails);
      } else {
        this.template.stateDefinition.push(stateDetails);
      }
      try {
        await ApiService.deviceTemplateUpdate(this.template);
        this.$emit('update-unsaved-changes', this.template);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.stateUpdated', {
          key: stateDetails.key
        }), 'success');
      } catch (err) {
        if (err.message === 'HEALTH_OPEARTOR_NOT_ALLOWED') {
          this.$snack(this.$i18n_t('Error.HEALTH_OPEARTOR_NOT_ALLOWED', {
            operator: get(err, 'response.data.data.operator'),
            valueType: get(err, 'response.data.data.valueType')
          }), 'error');
        } else {
          const errorText = this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.stateUpdatingFailed', {
            key: stateDetails.key
          });
          this.$snack(errorText, 'error');
        }
      } finally {
        this.stateEditDialog = false;
      }
    },
    initSortableTable() {
      const table = document.querySelector('.states-table tbody');
      Sortable.create(table, {
        handle: '.handle',
        onEnd: ({
          newIndex,
          oldIndex
        }) => {
          this.template.stateDefinition.splice(newIndex, 0, ...this.template.stateDefinition.splice(oldIndex, 1));
        }
      });
    },
    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.linkedStateAggregations = [];
    }
  }
};